import React, { useState, useContext, forwardRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Slide from '@mui/material/Slide';
import ItemView from './ItemView';
import AmmoLoadUnloadDialog from './AmmoLoadUnloadDialog';
import { AuthContext } from '../../contexts/AuthContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { 
    fetchItemUse, 
    withAuthCheck, 
    AuthError, 
    fetchItemGDeposit,
    fetchEquipItem,
    fetchUnequipItem
} from '../../services/api';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { useTranslation } from 'react-i18next';
import { v3Theme } from '../theme';
import { EQUIPMENT_STYLES } from '../constants/equipmentConstants';

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function ItemViewDetails({ open, onClose, item }) {
    const token = useContext(AuthContext);
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const [isProcessing, setIsProcessing] = useState(false);
    const [depositDialogOpen, setDepositDialogOpen] = useState(false);
    const [depositQty, setDepositQty] = useState(1);
    const [depositProcessing, setDepositProcessing] = useState(false);
    const [ammoDialogState, setAmmoDialogState] = useState({ open: false, action: '' });
    
    // Use empty object as fallback to keep dialog rendered during close animation
    const safeItem = item || { name: '', qty: 0, code: '', actions: [] };

    const handleUseClick = () => {
        if (!item || !item.code) return;
        setIsProcessing(true);
        withAuthCheck(fetchItemUse, token, item.code)
            .then(data => {
                onClose(); // Закрываем диалог
                setIsProcessing(false);
                showSnackbar(data.result, "success");
            })
            .catch(err => {
                onClose(); // Закрываем диалог
                setIsProcessing(false);
                if (err instanceof AuthError) {
                    // WebAuthForm покажется автоматически через AuthProvider
                    return;
                }
                const errorMessage = err.result || err.message || "error";
                showSnackbar(errorMessage, "error");
            });
    };

    const handleDepositClick = () => {
        setDepositQty(1);
        setDepositDialogOpen(true);
    };

    const handleDepositSubmit = () => {
        if (!item || !item.code || !depositQty || depositQty <= 0) return;
        setDepositProcessing(true);
        withAuthCheck(fetchItemGDeposit, token, item.code, Number(depositQty))
            .then(data => {
                setDepositProcessing(false);
                setDepositDialogOpen(false);
                onClose();
                showSnackbar(data.result || 'Deposited', 'success');
            })
            .catch(err => {
                setDepositProcessing(false);
                setDepositDialogOpen(false);
                onClose();
                if (err instanceof AuthError) return;
                const errorMessage = err.result || err.message || 'error';
                showSnackbar(errorMessage, 'error');
            });
    };

    const handleEquipClick = () => {
        if (!item || !item.code) return;
        setIsProcessing(true);
        withAuthCheck(fetchEquipItem, token, item.code)
            .then(data => {
                onClose();
                setIsProcessing(false);
                if (data.success) {
                    showSnackbar(data.result, 'success');
                } else {
                    showSnackbar(data.error || data.result || 'Operation failed', 'error');
                }
            })
            .catch(err => {
                onClose();
                setIsProcessing(false);
                if (err instanceof AuthError) return;
                const errorMessage = err.result || err.message || 'error';
                showSnackbar(errorMessage, 'error');
            });
    };

    const handleUnequipClick = () => {
        if (!item || !item.code) return;
        setIsProcessing(true);
        withAuthCheck(fetchUnequipItem, token, item.code)
            .then(data => {
                onClose();
                setIsProcessing(false);
                if (data.success) {
                    showSnackbar(data.result, 'success');
                } else {
                    showSnackbar(data.error || data.result || 'Operation failed', 'error');
                }
            })
            .catch(err => {
                onClose();
                setIsProcessing(false);
                if (err instanceof AuthError) return;
                const errorMessage = err.result || err.message || 'error';
                showSnackbar(errorMessage, 'error');
            });
    };

    const handleLoadClick = () => {
        setAmmoDialogState({ open: true, action: 'load' });
    };

    const handleUnloadClick = () => {
        setAmmoDialogState({ open: true, action: 'unload' });
    };


    // Проверяем, что массив actions существует и не пуст, и содержит "use"
    const hasUseAction =
        Array.isArray(safeItem.actions) && safeItem.actions.length > 0 && safeItem.actions.includes("use");
    // Проверяем, что есть gdeposit
    const hasGDepositAction =
        Array.isArray(safeItem.actions) && safeItem.actions.length > 0 && safeItem.actions.includes("gdeposit");
    // Проверяем остальные действия
    const hasEquipAction =
        Array.isArray(safeItem.actions) && safeItem.actions.length > 0 && safeItem.actions.includes("equip");
    const hasUnequipAction =
        Array.isArray(safeItem.actions) && safeItem.actions.length > 0 && safeItem.actions.includes("unequip");
    const hasLoadAction =
        Array.isArray(safeItem.actions) && safeItem.actions.length > 0 && safeItem.actions.includes("load");
    const hasUnloadAction =
        Array.isArray(safeItem.actions) && safeItem.actions.length > 0 && safeItem.actions.includes("unload");

    return (
        <>
            <Dialog 
                open={open} 
                onClose={onClose} 
                maxWidth="sm" 
                fullWidth
                TransitionComponent={Transition}
            >
                <DialogTitle
                    sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        fontFamily: EQUIPMENT_STYLES.FONT_FAMILY_QUANTITY
                    }}
                >
                    {safeItem.name || 'Loading...'} {safeItem.qty ? `(${safeItem.qty})` : ''}
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {/* Always show ItemView - it will handle loading state internally */}
                    <ItemView itemCode={safeItem.code || ''} />
                </DialogContent>
                {(hasUseAction || hasGDepositAction || hasEquipAction || hasUnequipAction || hasLoadAction || hasUnloadAction) && (
                    <DialogActions sx={{ justifyContent: 'flex-start', px: 3, mb:2, flexWrap: 'wrap', gap: 1 }}>
                        {hasUseAction && (
                            <Button
                                variant="contained"
                                onClick={handleUseClick}
                                disabled={isProcessing}
                                sx={{
                                    backgroundColor: v3Theme.main,
                                    color: '#fff',
                                    '&:hover': {
                                        backgroundColor: v3Theme.mainDark || '#1a2530',
                                    }
                                }}
                            >
                                {t('widgets.itemActions.use')}
                            </Button>
                        )}
                        {hasEquipAction && (
                            <Button
                                variant="contained"
                                onClick={handleEquipClick}
                                disabled={isProcessing}
                                sx={{
                                    backgroundColor: v3Theme.main,
                                    color: '#fff',
                                    '&:hover': {
                                        backgroundColor: v3Theme.mainDark || '#1a2530',
                                    }
                                }}
                            >
                                {t('widgets.itemActions.equip')}
                            </Button>
                        )}
                        {hasUnequipAction && (
                            <Button
                                variant="contained"
                                onClick={handleUnequipClick}
                                disabled={isProcessing}
                                sx={{
                                    backgroundColor: v3Theme.main,
                                    color: '#fff',
                                    '&:hover': {
                                        backgroundColor: v3Theme.mainDark || '#1a2530',
                                    }
                                }}
                            >
                                {t('widgets.itemActions.unequip')}
                            </Button>
                        )}
                        {hasLoadAction && (
                            <Button
                                variant="contained"
                                onClick={handleLoadClick}
                                disabled={isProcessing}
                                sx={{
                                    backgroundColor: v3Theme.main,
                                    color: '#fff',
                                    '&:hover': {
                                        backgroundColor: v3Theme.mainDark || '#1a2530',
                                    }
                                }}
                            >
                                {t('widgets.itemActions.load')}
                            </Button>
                        )}
                        {hasUnloadAction && (
                            <Button
                                variant="contained"
                                onClick={handleUnloadClick}
                                disabled={isProcessing}
                                sx={{
                                    backgroundColor: v3Theme.main,
                                    color: '#fff',
                                    '&:hover': {
                                        backgroundColor: v3Theme.mainDark || '#1a2530',
                                    }
                                }}
                            >
                                {t('widgets.itemActions.unload')}
                            </Button>
                        )}
                        {hasGDepositAction && (
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={handleDepositClick}
                                disabled={isProcessing}
                            >
                                {t('widgets.itemActions.depositToGuild')}
                            </Button>
                        )}
                    </DialogActions>
                )}
            </Dialog>
            {/* Deposit Dialog */}
            <Dialog 
                open={depositDialogOpen} 
                onClose={() => setDepositDialogOpen(false)} 
                maxWidth="xs" 
                fullWidth
                TransitionComponent={Transition}
            >
                <DialogTitle sx={{ fontFamily: EQUIPMENT_STYLES.FONT_FAMILY_QUANTITY }}>{t('widgets.itemActions.depositToGuild')}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2, fontFamily: EQUIPMENT_STYLES.FONT_FAMILY_QUANTITY }}>
                        <b> {safeItem.name} </b> × <b>{depositQty}</b>
                    </Typography>
                    <TextField
                        label={t('widgets.itemActions.quantity')}
                        InputLabelProps={{ sx: { fontFamily: EQUIPMENT_STYLES.FONT_FAMILY_QUANTITY } }}
                        type="number"
                        fullWidth
                        margin="normal"
                        value={depositQty}
                        onChange={e => setDepositQty(e.target.value)}
                        error={depositQty === '' || Number(depositQty) <= 0 || Number(depositQty) > safeItem.qty}
                        helperText={
                            depositQty === '' || Number(depositQty) <= 0
                                ? t('widgets.itemActions.enterQuantityGreaterThanZero')
                                : Number(depositQty) > safeItem.qty
                                    ? `${t('widgets.itemActions.maximum')}: ${safeItem.qty}`
                                    : ''
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end" sx={{ fontFamily: EQUIPMENT_STYLES.FONT_FAMILY_QUANTITY }}>
                                    {safeItem.code}
                                </InputAdornment>
                            ),
                            sx: { fontFamily: EQUIPMENT_STYLES.FONT_FAMILY_QUANTITY }
                        }}
                        disabled={depositProcessing}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleDepositSubmit}
                        disabled={
                            depositProcessing ||
                            depositQty === '' ||
                            Number(depositQty) <= 0 ||
                            Number(depositQty) > safeItem.qty
                        }
                        variant="contained"
                        sx={{
                            backgroundColor: v3Theme.main,
                            color: '#fff',
                            '&:hover': {
                                backgroundColor: v3Theme.mainDark || '#1a2530',
                            }
                        }}
                    >
                        {t('widgets.itemActions.yes')}
                    </Button>
                    <Button onClick={() => setDepositDialogOpen(false)} disabled={depositProcessing}>{t('widgets.itemActions.no')}</Button>
                </DialogActions>
            </Dialog>
            {/* Ammo Load/Unload Dialog - Only render when actually open */}
            {ammoDialogState.open && (
                <AmmoLoadUnloadDialog 
                    open={true}
                    onClose={() => {
                        setAmmoDialogState({ open: false, action: '' });
                        onClose(); // Close parent dialog as well
                    }}
                    item={{
                        ...safeItem,
                        qty: safeItem.qty || safeItem.quantity,
                        quantity: safeItem.quantity || safeItem.qty,
                        maxLoadable: safeItem.maxLoadable
                    }}
                    action={ammoDialogState.action}
                />
            )}
            <Backdrop
                open={isProcessing || depositProcessing}
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 999 }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    );
}